import React, { useState } from 'react';
import { API_BASE_URL } from '../config/platformConfig';
import { getAuthToken } from '../services/api';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface UpdateStageModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: string;
  currentStage: string;
  onStageUpdated: () => void;
}

const UpdateStageModal: React.FC<UpdateStageModalProps> = ({
  isOpen,
  onClose,
  leadId,
  currentStage,
  onStageUpdated
}) => {
  const [formData, setFormData] = useState({
    stage: currentStage,
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const stages = [
    { value: 'new', label: 'New', description: 'Lead just created' },
    { value: 'contacted', label: 'Contacted', description: 'Initial contact made' },
    { value: 'visit_scheduled', label: 'Visit Scheduled', description: 'School visit booked' },
    { value: 'applied', label: 'Applied', description: 'Application submitted' },
    { value: 'admitted', label: 'Admitted', description: 'Student admitted' },
    { value: 'rejected', label: 'Rejected', description: 'Application rejected' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = await getAuthToken();
      const response = await fetch(`${API_BASE_URL}/leads/${leadId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update stage');
      }

      // Show success message
      let successMsg = 'Lead stage updated successfully!';
      if (data.data.applicationCreated) {
        successMsg += ' Application auto-created from this lead.';
      }
      setSuccess(successMsg);

      setFormData({
        stage: currentStage,
        notes: ''
      });

      setTimeout(() => {
        onStageUpdated();
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-900">Update Lead Stage</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
            ✓ {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select New Stage *
            </label>
            <div className="space-y-2">
              {stages.map((stage) => (
                <label
                  key={stage.value}
                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.stage === stage.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="stage"
                    value={stage.value}
                    checked={formData.stage === stage.value}
                    onChange={(e) => setFormData({ ...formData, stage: e.target.value })}
                    className="mt-1"
                  />
                  <div className="ml-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">{stage.label}</span>
                      {stage.value === currentStage && (
                        <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                          Current
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">{stage.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input-field"
              rows={3}
              placeholder="Add any notes about this stage change..."
            />
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || formData.stage === currentStage}
            >
              {loading ? 'Updating...' : 'Update Stage'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateStageModal;
